import { create } from 'zustand';

/**
 * Save state published by the active object form for the object-level toolbar.
 *
 * The toolbar renders in the page header — *above* `ControlledTabs` and so
 * outside every `RiskSmartFormProvider`, which is mounted per-tab inside
 * `FormContext`. React context only flows down, so the toolbar cannot read the
 * active tab's form state directly. This store bridges that gap: the form
 * publishes its save state upward (see `FormContext`, gated on
 * `publishToolbarState`) and the toolbar reads it here.
 *
 * Single-slot by design — Cloudscape `Tabs` mounts only the active tab, so at
 * most one savable form is live at a time. The publishing form clears the slot
 * on unmount (tab switch / navigation), so a non-saving tab leaves
 * `formState === null` and the toolbar greys the save disc.
 */
export interface ObjectToolbarFormState {
  /** The mounted form is editable (not read-only). false → save disc greyed. */
  supported: boolean;
  /** The form has unsaved changes (drives the save disc's attention pulse). */
  dirty: boolean;
  /** A save is in flight. */
  loading: boolean;
  /** Submits the active form — wired to the toolbar's save disc. */
  onSave: () => void;
}

/**
 * Form-editor opener published by the active form's `FormMenu` so the page-header
 * toolbar can launch the form editor (`FormEditorDialog`) from its overflow
 * "Edit" group. Independent of `formState`: a different publisher (`FormMenu`,
 * not `FormContext`) with its own lifecycle, so it has its own setter/clearer
 * and the two slots never clobber each other.
 */
export interface ObjectToolbarFormConfigState {
  /** Opens the active form's editor dialog. */
  openConfigure: () => void;
}

export interface ObjectToolbarFormStore {
  /** Save state of the active object form, or `null` when none is mounted. */
  formState: ObjectToolbarFormState | null;
  /** Publish (or replace) the active form's save state. */
  setFormState: (state: ObjectToolbarFormState) => void;
  /** Clear the slot — called by the publishing form on unmount. */
  clearFormState: () => void;
  /**
   * Form-editor opener for the active form, or `null` when none is published
   * (no editable FormMenu mounted, or the relocate flags are off).
   */
  formConfigState: ObjectToolbarFormConfigState | null;
  /** Publish (or replace) the active form's editor opener. */
  setFormConfigState: (state: ObjectToolbarFormConfigState) => void;
  /** Clear the slot — called by the publishing FormMenu on unmount. */
  clearFormConfigState: () => void;
}

export const useObjectToolbarFormStore = create<ObjectToolbarFormStore>(
  (set) => ({
    formState: null,
    setFormState: (state) => set({ formState: state }),
    clearFormState: () => set({ formState: null }),
    formConfigState: null,
    setFormConfigState: (state) => set({ formConfigState: state }),
    clearFormConfigState: () => set({ formConfigState: null }),
  })
);
